import React from "react";
import { useSearchParams } from "react-router-dom";
import { CV, CVProps } from "../components/cv";
import VincentLImg from "@/assets/images/vincent-l.png";

const shared = {
  name: "Vincent Lam",
  photo: VincentLImg,
  education: [
    {
      degree: "Master of Science in Industrial Engineering and Management",
      institution:
        "Blekinge Institute of Technology (BTH), with Specialization in Applied IT",
      year: "2019",
    },
  ],
} satisfies Partial<CVProps>;

const variantOptions = [
  { label: "Full-Stack", value: "default" },
  { label: "Backstage / Platform", value: "backstage" },
];

const variants: Record<string, Omit<CVProps, keyof typeof shared>> = {
  default: {
    title: "Full-Stack Developer",
    subtitle:
      "Web frameworks and API integrations across the stack — engagements at E.ON, IKEA and Axis Communications",
    quote: {
      text: "Vincent was a great asset to our team, not only consistently delivering high-quality work with a solution-oriented, \"can-do\" mindset but also brings a very positive attitude to the team as a great colleague and friend. He knows how to navigate complex integrations and create enhanced developer experience, collaborating seamlessly both within the team and with other developers while taking ownership of his deliveries.",
      author: "Niclas Strandéus",
      authorTitle: "Engineering Manager, Ingka Group (IKEA) – Developer Portal project",
    },
    summary:
      "Full-stack developer with 10+ years of experience building web applications and APIs. Strong across both front-end and back-end, with a focus on scalable, maintainable solutions. Uses AI tools as part of the development workflow for code generation, debugging, and rapid prototyping.",
    skills: {
      Languages: ["Java", "Typescript/Javascript", "C#/.NET", "Go", "Python", "PHP"],
      Frontend: ["React", "Redux", "Next.js", "Vue", "Nuxt.js", "HTML/CSS"],
      Backend: ["Spring/Springboot", "Node.js", "GraphQL", "SQL", "API Integration"],
      Database: ["MySQL", "PostgreSQL", "Strapi", "MongoDB", "Firebase"],
      "Cloud & DevOps": ["Azure", "GCP", "Docker", "Terraform"],
      Platforms: ["Backstage", "Drupal", "Postscript"],
      Testing: ["Jest", "JUnit"],
    },
    experience: [
      {
        role: "System Developer",
        company: "E.ON Sverige",
        period: "Nov 2023 – Oct 2025",
        description:
          "Part of a team building and maintaining business-critical API integrations between SAP, Salesforce, and internal systems, as part of E.ON's modernization of its integration landscape.",
        highlights: [
          "Transformed complex data in C#/.NET, leveraged Entity Framework Core for data access, and built reliable REST APIs for high-volume data exchange",
          "Developed API-based features in a middleware platform enabling reliable data synchronization between SAP and Salesforce",
          "Designed and optimized complex data transformations to improve scalability and robustness of integration flows",
          "Refactored legacy Java/Spring APIs to Spring Boot as part of an API Hub modernization initiative",
          "Implemented BankID functionality and Swedish e-identification (e-legitimation) flows in E.ON's internal APIs",
          "Worked with IAM in the Azure environment to secure access across integration services",
          "Improved performance and maintainability of integration services used daily across the organization",
          "Tech: Typescript/Javascript, Node.js, Java, Spring/Springboot, C#, .NET, Entity Framework Core, Go, Azure, IAM, BankID, Terraform, Docker, Salesforce, SAP",
        ],
      },
      {
        role: "Full Stack Developer",
        company: "INGKA Group (IKEA)",
        period: "Apr 2022 – Sep 2023",
        description:
          "Contributed to the development of an internal developer portal used to standardize workflows and improve productivity across engineering teams.",
        highlights: [
          "Built features in React and Backstage to streamline developer workflows and improve self-service capabilities",
          "Automated cloud infrastructure using Terraform within the GCP ecosystem",
          "Improved onboarding experience by enhancing documentation, usability, and developer tooling",
          "Established shared components and best practices to increase consistency across teams",
          "Tech: Typescript/Javascript, Java, React, Backstage, Node.js, Express, Go, Python, HTML/CSS, GCP",
        ],
      },
      {
        role: "Full Stack Web Developer",
        company: "Axis Communications",
        period: "Jun 2021 – Feb 2022",
        description:
          "Worked on axis.com, improving performance, scalability, and user experience for a global, high-traffic website.",
        highlights: [
          "Developed frontend and backend features for marketing campaigns and product launches using React, TypeScript, and Drupal/PHP",
          "Optimized SQL queries and caching strategies to reduce load times and improve performance",
          "Delivered responsive and consistent UI across devices and browsers",
          "Refactored legacy code to reduce complexity and improve maintainability",
          "Tech: Drupal, PHP, React, TypeScript, SQL, HTML/CSS, Docker",
        ],
      },
      {
        role: "Electronics Manufacturing",
        company: "NOTE Lund AB",
        period: "Jan 2020 – Aug 2020",
        description:
          "Worked with production and testing of electronic components, including PCBs, in a high-quality manufacturing environment.",
        highlights: [
          "Programmed, tested, and assembled electronic products and circuit boards",
          "Ensured quality and reliability through systematic testing and verification processes",
          "Collaborated with production teams to troubleshoot and resolve hardware issues",
        ],
      },
    ],
  },

  backstage: {
    title: "Platform Developer",
    subtitle:
      "Backstage, developer portals and platform engineering — engagement at INGKA Group (IKEA)",
    quote: {
      text: "Vincent was a great asset to our team, not only consistently delivering high-quality work with a solution-oriented, \"can-do\" mindset but also brings a very positive attitude to the team as a great colleague and friend. He knows how to navigate complex integrations and create enhanced developer experience, collaborating seamlessly both within the team and with other developers while taking ownership of his deliveries.",
      author: "Niclas Strandéus",
      authorTitle: "Engineering Manager, Ingka Group (IKEA) – Developer Portal project",
    },
    summary:
      "Platform developer with 10+ years of experience in software development and a strong focus on developer experience and internal tooling. Experienced in building and extending Backstage-based developer portals, automating infrastructure, and improving developer workflows at scale. Uses AI tools as part of the development workflow for code generation, debugging, and rapid prototyping.",
    skills: {
      Languages: ["Java", "Typescript/Javascript", "C#/.NET", "Go", "Python"],
      Frontend: ["React", "Redux", "Next.js", "HTML/CSS"],
      "Platform & Tooling": ["Backstage", "Spring/Springboot", "Node.js", "Express", "GraphQL", "Postscript"],
      Database: ["MySQL", "PostgreSQL", "Strapi", "MongoDB", "Firebase"],
      "Cloud & DevOps": ["GCP", "Azure", "Docker", "Terraform"],
      Testing: ["Jest", "JUnit"],
    },
    experience: [
      {
        role: "Full Stack Developer — Developer Portal",
        company: "INGKA Group (IKEA)",
        period: "Apr 2022 – Sep 2023",
        description:
          "Key contributor to the development of an internal Backstage-based developer portal serving engineering teams across the organization, improving standardization, self-service, and developer productivity.",
        highlights: [
          "Built and extended Backstage plugins and features to streamline developer workflows and enable self-service provisioning",
          "Developed custom React components within the Backstage framework to improve usability and consistency across teams",
          "Automated cloud infrastructure provisioning using Terraform within the GCP ecosystem, integrated into the portal",
          "Improved developer onboarding by enhancing portal documentation, templates, and tooling",
          "Established shared components and best practices to increase consistency and reduce duplication across teams",
          "Tech: Typescript/Javascript, Java, React, Backstage, Node.js, Express, Go, Python, HTML/CSS, GCP, Terraform",
        ],
      },
      {
        role: "System Developer",
        company: "E.ON Sverige",
        period: "Nov 2023 – Oct 2025",
        description:
          "Part of a team building and maintaining business-critical API integrations between SAP, Salesforce, and internal systems.",
        highlights: [
          "Transformed complex data in C#/.NET, leveraged Entity Framework Core for data access, and built reliable REST APIs for high-volume data exchange",
          "Developed API-based features in a middleware platform enabling reliable data synchronization between SAP and Salesforce",
          "Designed and optimized complex data transformations to improve scalability and robustness of integration flows",
          "Refactored legacy Java/Spring APIs to Spring Boot as part of an API Hub modernization initiative",
          "Implemented BankID and Swedish e-identification (e-legitimation) flows in E.ON's internal APIs",
          "Worked with IAM in the Azure environment to secure access across integration services",
          "Tech: Typescript/Javascript, Node.js, Java, Spring/Springboot, C#, .NET, Entity Framework Core, Go, Azure, IAM, BankID, Terraform, Docker",
        ],
      },
      {
        role: "Full Stack Web Developer",
        company: "Axis Communications",
        period: "Jun 2021 – Feb 2022",
        description:
          "Worked on axis.com, improving performance, scalability, and user experience for a global, high-traffic website.",
        highlights: [
          "Developed frontend and backend features using React, TypeScript, and Drupal/PHP",
          "Optimized SQL queries and caching strategies to reduce load times",
          "Tech: Drupal, PHP, React, TypeScript, SQL, HTML/CSS, Docker",
        ],
      },
      {
        role: "Electronics Manufacturing",
        company: "NOTE Lund AB",
        period: "Jan 2020 – Aug 2020",
        description:
          "Worked with production and testing of electronic components, including PCBs, in a high-quality manufacturing environment.",
        highlights: [
          "Programmed, tested, and assembled electronic products and circuit boards",
          "Ensured quality and reliability through systematic testing and verification processes",
        ],
      },
    ],
  },
};

export default function CVVincentLam() {
  const [searchParams, setSearchParams] = useSearchParams();
  const variantKey = searchParams.get("variant") || "default";
  const variant = variants[variantKey] || variants.default;

  React.useEffect(() => {
    document.title = "BoldBrave CV - Vincent Lam";
  }, []);

  return (
    <CV
      {...shared}
      {...variant}
      variants={variantOptions}
      currentVariant={variantKey}
      onVariantChange={(v) => {
        if (v === "default") {
          setSearchParams({});
        } else {
          setSearchParams({ variant: v });
        }
      }}
    />
  );
}
